/**
 * GlassPageHeader — TOP PRIORITY visual anchor for every page.
 *
 * Design rationale: the user's primary complaint was that page titles were
 * washing into the pale background. This component solves that with:
 *   - a heavily-contrasted translucent header card that floats above content
 *   - a luminous icon badge on the left (optional)
 *   - a mono-eyebrow label for category/section context
 *   - a large Outfit-font title (H1-scale, high contrast)
 *   - a secondary subtitle that stays readable but doesn't compete
 *   - an actions slot on the right for CTAs / toggles / status
 *
 * Usage:
 *   <GlassPageHeader
 *     eyebrow="Analysis workspace"
 *     title="Analyze"
 *     subtitle="Score AI output against the 6-dimension rubric."
 *     icon={<Activity />}
 *     actions={<SaveStateChip />}
 *   />
 */
import React from 'react';

export function GlassPageHeader({
  eyebrow,
  title,
  subtitle,
  icon,
  actions,
  className = '',
}) {
  return (
    <header
      className={`liquid-header ${className}`}
      data-testid="glass-page-header"
    >
      <div className="relative flex items-start gap-4 sm:gap-5">
        {icon && (
          <div
            className="shrink-0 mt-1 w-11 h-11 rounded-2xl flex items-center justify-center text-[var(--ink-accent)] border border-[var(--glass-stroke-strong)] bg-[linear-gradient(135deg,rgba(255,255,255,0.12),rgba(255,255,255,0.02))] shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_8px_18px_hsl(var(--accent-cyan)/0.18)]"
            aria-hidden
            data-testid="glass-page-header-icon"
          >
            {React.isValidElement(icon)
              ? React.cloneElement(icon, { className: 'w-5 h-5' })
              : icon}
          </div>
        )}
        <div className="flex-1 min-w-0">
          {eyebrow && (
            <div className="liquid-header__eyebrow" data-testid="glass-page-header-eyebrow">
              <span
                className="inline-block w-1.5 h-1.5 rounded-full bg-[hsl(var(--accent-cyan))]"
                style={{ boxShadow: '0 0 10px hsl(var(--accent-cyan))' }}
                aria-hidden
              />
              {eyebrow}
            </div>
          )}
          <h1 className="liquid-header__title" data-testid="glass-page-header-title">
            {title}
          </h1>
          {subtitle && (
            <p className="liquid-header__subtitle" data-testid="glass-page-header-subtitle">
              {subtitle}
            </p>
          )}
        </div>
        {actions && (
          <div className="shrink-0 flex items-center gap-2" data-testid="glass-page-header-actions">
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}

export default GlassPageHeader;
